/**
 * CustomDraftPicker — the "Draft more sections" picker for a CUSTOM submission.
 *
 * A custom run's spine is the writer's OWN document headings (``CUSTOM-N``), not
 * the doctype template tree that {@link LoomSelection} renders — so that picker
 * can't be used here. This is the custom analogue: a flat checklist of the
 * document's sections where ONLY sections that have a saved drafting instruction
 * (authored in the Inspector's Instructions tab) are selectable. Sections with
 * no instruction, or already drafted, are shown disabled with the reason, so the
 * writer sees the whole outline but can only draft what Asthra has instructions
 * for.
 *
 * SRP: pure presentation. The shell owns the dispatch (customDraftSections via
 * actuallyStartRun) and the run lifecycle.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useMemo, useState } from "react";
import type { SpineSection } from "../hooks/useVariantCRun";
import { formatGutterNumber } from "../sectionLabel";
import { RUN_INSTRUCTIONS_MAX_CHARS } from "./LoomSelection";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  dialog: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    padding: "18px 18px 16px",
    maxWidth: "460px",
    width: "100%",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  title: {
    fontFamily: fonts.display,
    fontSize: "15px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "4px",
  },
  intro: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
    marginBottom: "10px",
  },
  list: {
    overflowY: "auto",
    borderTop: `1px solid ${surfaceColors.line}`,
    marginTop: "2px",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "8px 4px",
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  rowDisabled: {
    opacity: 0.55,
  },
  checkbox: {
    marginTop: "2px",
    cursor: "pointer",
  },
  checkboxDisabled: {
    cursor: "not-allowed",
  },
  rowMain: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  rowTitleLine: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
  },
  gutter: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    whiteSpace: "nowrap",
  },
  rowTitle: {
    fontSize: "12.5px",
    color: inkColors.ink,
    fontWeight: 600,
  },
  reason: {
    fontSize: "10.5px",
    color: inkColors.ink4,
    marginTop: "2px",
  },
  commentsLabel: {
    display: "block",
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink2,
    marginTop: "12px",
    marginBottom: "5px",
  },
  commentsInput: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "54px",
    maxHeight: "140px",
    resize: "vertical",
    padding: "7px 9px",
    fontSize: "12px",
    lineHeight: 1.4,
    fontFamily: fonts.body,
    color: inkColors.ink,
    background: "#fff",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "3px",
    "&:focus": { outline: "none", border: `1px solid ${brandColors.primary}` },
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
  },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: {
    background: brandColors.primary,
    color: "#fff",
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  btnSecondary: {
    background: "#fff",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
});

/** A section is drafted (has content) when its status is past ``queued`` —
 *  the custom spine marks content-bearing headings ``drafted``. */
function isDrafted(status: SpineSection["status"]): boolean {
  return status === "drafted" || status === "needs_approval" || status === "flagged";
}

export interface CustomDraftPickerProps {
  /** The custom run's sections (document-order spine rows). */
  sections: readonly SpineSection[];
  /** Draft the chosen sections — shell dispatches customDraftSections.
   *  ``runInstructions`` is the writer's trimmed final comments (absent when
   *  left empty), appended to every chosen section's instruction. */
  onStart: (sectionIds: string[], runInstructions?: string) => void;
  /** Dismiss without drafting. */
  onCancel: () => void;
}

export const CustomDraftPicker: React.FC<CustomDraftPickerProps> = ({ sections, onStart, onCancel }) => {
  const styles = useStyles();

  // A section is selectable only when it has a saved instruction AND hasn't
  // been drafted yet. Everything else renders disabled with the reason.
  const draftable = useMemo(
    () => new Set(sections.filter((s) => Boolean(s.instruction?.trim()) && !isDrafted(s.status)).map((s) => s.id)),
    [sections],
  );

  // Default: all draftable sections checked.
  const [checked, setChecked] = useState<Set<string>>(() => new Set(draftable));
  const [runInstructions, setRunInstructions] = useState("");

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = checked.size;

  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="custom-draft-picker-title">
      <div className={styles.dialog}>
        <div id="custom-draft-picker-title" className={styles.title}>
          Draft sections
        </div>
        <div className={styles.intro}>
          Only sections with a saved instruction can be drafted — the drafting agent drafts them from your source library using the
          instruction you wrote. Add an instruction in a section&rsquo;s Instructions tab to unlock it here.
        </div>
        <div className={styles.list}>
          {sections.map((s) => {
            const canDraft = draftable.has(s.id);
            const reason = !s.instruction?.trim()
              ? "No instruction yet — add one in the Instructions tab."
              : isDrafted(s.status)
                ? "Already drafted."
                : null;
            return (
              <label
                key={s.id}
                className={mergeClasses(styles.row, !canDraft && styles.rowDisabled)}
                aria-disabled={!canDraft}
              >
                <input
                  type="checkbox"
                  className={mergeClasses(styles.checkbox, !canDraft && styles.checkboxDisabled)}
                  checked={canDraft && checked.has(s.id)}
                  disabled={!canDraft}
                  onChange={() => canDraft && toggle(s.id)}
                />
                <span className={styles.rowMain}>
                  <span className={styles.rowTitleLine}>
                    <span className={styles.gutter}>{formatGutterNumber(s.number)}</span>
                    <span className={styles.rowTitle}>{s.title || s.id}</span>
                  </span>
                  {reason && <span className={styles.reason}>{reason}</span>}
                </span>
              </label>
            );
          })}
        </div>
        <label className={styles.commentsLabel} htmlFor="custom-draft-run-instructions">
          Final comments for the drafters (optional — applied to every selected section)
        </label>
        <textarea
          id="custom-draft-run-instructions"
          className={styles.commentsInput}
          value={runInstructions}
          maxLength={RUN_INSTRUCTIONS_MAX_CHARS}
          onChange={(e) => setRunInstructions(e.target.value)}
          placeholder="e.g. Keep the existing terminology. Report durations in weeks."
          rows={2}
        />
        <div className={styles.actions}>
          <button type="button" className={mergeClasses(styles.btn, styles.btnSecondary)} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnPrimary)}
            disabled={selectedCount === 0}
            onClick={() => onStart([...checked], runInstructions.trim() || undefined)}
          >
            {selectedCount === 0 ? "Draft sections" : `Draft ${selectedCount} section${selectedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default CustomDraftPicker;
